import React, { createContext, ReactNode, useContext } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

import {
  PageConfigName,
  PageConfigurations,
  PageDirectory,
  PageDirectoryEntryName,
} from "../api/PageDirectory";
import { Config } from "../config";
import { hasPermission } from "../helpers/user";
import { ApplicationScope, AuthScope } from "../types/App";
import { User } from "../types/User";

// Define the context with a fetch function
const AuthContext = createContext<{
  inAppScope: (scope: ApplicationScope) => boolean;
  isAuthorised: (scope: AuthScope, user: User) => boolean;
  getScopedPageUrl: (
    entry: PageDirectoryEntryName,
    entityId?: string,
    secondaryId?: string
  ) => string;
  getScopedPageConfig: <T>(configName: PageConfigName) => T;
}>({
  inAppScope: () => false,
  isAuthorised: () => false,
  getScopedPageUrl: () => "",
  getScopedPageConfig: <T,>() => "" as T,
});

// Custom hook to use the Auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth Provider component
export const AuthProvider: React.FC<{
  pageDirectory: PageDirectory;
  pageConfigurations: PageConfigurations;
  children: ReactNode;
}> = ({ pageDirectory, pageConfigurations, children }) => {
  const auth = useOidcAuth();

  const inAppScope = (scope: ApplicationScope) => {
    return scope === (Config.applicationScope || "external");
  };

  const isAuthorised = (authScope: AuthScope, user: User) => {
    // 1. Access the decoded token via auth.user.profile
    const profile = auth.user?.profile as unknown as {
      resource_access: { "auto-pilot-portal": { roles: string[] } };
    };

    // 2. Extract roles from the same Keycloak path
    const roles = (profile?.resource_access?.["auto-pilot-portal"]?.roles ||
      []) as string[];

    let isAuthorised = false;

    switch (authScope) {
      case "external":
        isAuthorised = true;
        break;
      case "external-employer":
        isAuthorised =
          !!user.accountPermissions &&
          hasPermission(user.accountPermissions, "accessEmployer");
        break;
      case "internal":
        isAuthorised = roles.includes("internal-portal-user");
        break;
    }

    return isAuthorised;
  };

  const getScopedPageUrl = (
    directoryEntry: PageDirectoryEntryName,
    entityId?: string,
    secondaryId?: string
  ): string => {
    const entry = pageDirectory[directoryEntry];
    if (entry) {
      return entry(entityId, secondaryId);
    } else {
      // If no entry is found then redirect to home
      return pageDirectory.home?.() ?? "";
    }
  };

  const getScopedPageConfig = <T,>(configName: PageConfigName): T => {
    const config = pageConfigurations[configName];

    if (config) {
      return pageConfigurations[configName] as T;
    } else {
      throw Error(`No configuration found, entry: ${configName}`);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        inAppScope,
        isAuthorised,
        getScopedPageUrl,
        getScopedPageConfig,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
